/**
 * Le passage par l'écran du code, et le retour.
 *
 * On n'arrive pas sur cet écran pour créer un code : on y arrive en voulant <b>protéger une
 * fiche</b>, et il a fallu créer un code en chemin. L'intention doit donc faire l'aller-retour,
 * sinon on revient sur une fiche toujours ouverte, à tout reprendre (correction S100).
 *
 * Elle voyage dans l'adresse plutôt que dans l'état du routeur : un rafraîchissement en cours de
 * saisie ne la perd pas.
 *
 * Les deux sens sont ici, côte à côte et testés ensemble. C'est tout l'intérêt : un nom de
 * paramètre écrit d'un côté et relu de l'autre finit par diverger, et le défaut serait muet —
 * la page créerait le code, ne verrouillerait rien, et personne ne saurait pourquoi.
 */

/** La fiche qu'on venait protéger. */
export type FicheAProteger = { readonly espace: string; readonly fiche: string }

/** Ce que l'écran du code a trouvé dans son adresse. */
export type IntentionDuCode = {
  /** Où revenir, ou `null` si rien d'exploitable n'a été transmis. */
  readonly retour: string | null
  /** La fiche à verrouiller dès le code enregistré, ou `null` si on venait juste créer un code. */
  readonly aProteger: FicheAProteger | null
}

/** L'adresse de l'écran du code, portant de quoi reprendre l'intention au retour. */
export function lienDuCode(retour: string, aProteger?: FicheAProteger): string {
  const parametres = new URLSearchParams({ retour })

  if (aProteger) {
    parametres.set('espace', aProteger.espace)
    parametres.set('fiche', aProteger.fiche)
  }

  return `/code-confidentiel?${parametres.toString()}`
}

/**
 * Relit l'intention depuis l'adresse.
 *
 * Le retour n'est retenu que s'il reste dans l'application : une adresse complète glissée dans
 * le paramètre enverrait ailleurs, et ce serait une porte ouverte sur n'importe quel site. Le
 * doublé `//` est écarté avec le reste — « //ailleurs.example » est une adresse de protocole
 * relatif, qui sort du site tout en commençant par une barre.
 */
export function intentionDuCode(parametres: URLSearchParams): IntentionDuCode {
  const demande = parametres.get('retour')
  const retour = demande?.startsWith('/') && !demande.startsWith('//') ? demande : null

  const espace = parametres.get('espace')
  const fiche = parametres.get('fiche')

  return { retour, aProteger: espace && fiche ? { espace, fiche } : null }
}
